import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ChevronLeftIcon, ChevronRightIcon, XIcon } from 'lucide-react';
import { Avatar } from '../components/Avatar';
import { Button } from '../components/ui/Button';
import { findPerson } from '../data/people';
import { useLocale } from '../contexts/LocaleContext';
import { useSocial } from '../contexts/SocialContext';
const ITEM_MS = 5000;
export function PulseViewer() {
  const {
    pulseId = ''
  } = useParams();
  const {
    t,
    tl
  } = useLocale();
  const navigate = useNavigate();
  const social = useSocial();
  const [index, setIndex] = useState(0);
  const ring = social.pulses.find(item => item.id === pulseId);
  useEffect(() => {
    if (!ring) return;
    const timer = window.setTimeout(() => {
      if (index < ring.items.length - 1) setIndex(index + 1);else navigate(-1);
    }, ITEM_MS);
    return () => window.clearTimeout(timer);
  }, [index, ring, navigate]);
  if (!ring) {
    return <div className="grid min-h-screen place-items-center bg-deeper px-3 text-center" data-id="element-3652" data-mp="2qszkv9v6cf0k">
        <div data-id="element-3653" data-mp="2ls2491ptbt16">
          <p className="text-[15px] font-medium text-sand" data-id="element-3654" data-mp="2lrjxhb5ixwf0">{t('pulse.missing')}</p>
          <Button variant="gold" className="mt-2" onClick={() => navigate('/feed')} data-id="element-3655" data-mp="2hsjjq71wljyv">
            {t('pulse.backToFeed')}
          </Button>
        </div>
      </div>;
  }
  const author = findPerson(ring.authorId);
  const item = ring.items[index];
  return <div className="flex min-h-screen w-full justify-center bg-deeper" data-id="element-3656" data-mp="35hcgg5qcnvuj">
      <div className="relative flex min-h-screen w-full max-w-app flex-col bg-deep" data-id="element-3657" data-mp="35j71dk1mfnym">
        <div className="flex gap-1 px-2 pt-3" data-id="element-3658" data-mp="30ojc3iysmpiz">
          {ring.items.map((entry, i) => <span key={entry.id} className="h-[3px] flex-1 overflow-hidden rounded-pill bg-white/20" data-id="element-3659" data-mp="2vqrjemqbqzdq" data-mpi={i}>
              {i < index && <span className="block h-full w-full bg-gold" data-id="element-3660" data-mp="3off0e641j2lh" />}
              {i === index && <motion.span key={`${entry.id}-active`} className="block h-full bg-gold" initial={{
            width: '0%'
          }} animate={{
            width: '100%'
          }} transition={{
            duration: ITEM_MS / 1000,
            ease: 'linear'
          }} data-id="element-3661" data-mp="b6axbo6kdloj" />}
            </span>)}
        </div>

        <header className="flex items-center gap-2 px-2 py-2" data-id="element-3662" data-mp="6dq5utb4rko6">
          <Avatar src={author.avatar} name={author.name} size={36} data-id="element-3663" data-mp="3okxu237kh29g" />
          <div className="min-w-0 flex-1" data-id="element-3664" data-mp="3tlbrkrbjktt8">
            <p className="truncate text-[13.5px] font-medium text-sand" data-id="element-3665" data-mp="3tm3n62552rji">
              {author.name}
            </p>
            <p className="truncate text-[11px] text-ondark" data-id="element-3666" data-mp="3fn3velmbxwj4">{author.username}</p>
          </div>
          <button type="button" onClick={() => navigate(-1)} aria-label={t('common.close')} className="grid h-11 w-11 place-items-center rounded-pill text-ondark transition-colors duration-150 ease-brand hover:text-sand" data-id="element-3667" data-mp="3jop7w5uyc8ss">
            <XIcon size={19} data-id="element-3668" data-mp="32dtsqvsvn38d" />
          </button>
        </header>

        <div className="relative flex-1 overflow-hidden" data-id="element-3669" data-mp="2ychxoar8hpss">
          <img src={item.media} alt={tl(item.label)} className="absolute inset-0 h-full w-full object-cover" data-id="element-3670" data-mp="2y9vqwdc63ebm" />
          <span className="absolute inset-0 bg-deep/25" aria-hidden="true" data-id="element-3671" data-mp="37blgreqei57a" />

          <button type="button" onClick={() => setIndex(Math.max(0, index - 1))} aria-label={t('pulse.previous')} className="absolute bottom-0 left-0 top-0 grid w-1/3 place-items-start pl-1 pt-3 text-sand/0 hover:text-sand/70" data-id="element-3672" data-mp="3m0hyy4m68ylm">
            <ChevronLeftIcon size={20} data-id="element-3673" data-mp="3q2cyb3kjl1wr" />
          </button>
          <button type="button" onClick={() => index < ring.items.length - 1 ? setIndex(index + 1) : navigate(-1)} aria-label={t('pulse.next')} className="absolute bottom-0 right-0 top-0 grid w-1/3 place-items-end pr-1 pt-3 text-sand/0 hover:text-sand/70" data-id="element-3674" data-mp="3q0s3dcubfgyd">
            <ChevronRightIcon size={20} data-id="element-3675" data-mp="224khzdp2w9l7" />
          </button>
        </div>

        <div className="px-2 pb-3 pt-2" data-id="element-3676" data-mp="2746xxuwak3xp">
          <div className="rounded-panel border border-white/10 bg-deeper/80 p-2" data-id="element-3677" data-mp="2758ay4wvb0i2">
            <p className="bee-overline text-[9px] font-bold text-gold" data-id="element-3678" data-mp="2c3t7nucwv21r">
              {t(`category.${item.kind}`)}
            </p>
            <p className="mt-1 text-[15px] font-bold text-sand" data-id="element-3679" data-mp="1eh41r9f385am">{tl(item.label)}</p>
            {item.metric && <p className="bee-num mt-[2px] text-[12.5px] text-ondark" data-id="element-3680" data-mp="1jevs3z033jcp">
                {tl(item.metric)}
              </p>}
            <Button variant="gold" fullWidth className="mt-2" onClick={() => navigate(`/u/${author.username.replace('@', '')}`)} data-id="element-3681" data-mp="1t45mxmmbbg4i">
              {t('pulse.viewProfile')}
            </Button>
          </div>
        </div>
      </div>
    </div>;
}